/**
 * MonitorHealthWidget — Crash/restart counts and last event
 * Shows: Events/hour, crashes today, restarts today, last event, per-monitor restart state
 * Collapsible — click header to expand/collapse
 * Refresh: 30s
 * CSS prefix: mhw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchHealth } from '../lib/api'
import { Activity, AlertTriangle, RotateCcw, RefreshCw, Zap, ChevronRight } from 'lucide-react'
import './MonitorHealthWidget.css'

function MonitorHealthWidget() {
  const [health, setHealth] = useState(null)
  const [status, setStatus] = useState('unknown')
  const [proxyStatus, setProxyStatus] = useState(null)
  const [lastCheck, setLastCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const res = await fetchHealth()

    setProxyStatus(res.status)

    if (res.status === 'ok' && res.data) {
      setHealth(res.data)

      const typeCounts = res.data.type_counts_last_hour || {}
      if ((typeCounts.CRASH || 0) > 0) setStatus('error')
      else if ((typeCounts.RESTART || 0) > 2) setStatus('warning')
      else setStatus('online')
    } else {
      setHealth(null)
      setStatus(res.status === 'sleeping' ? 'sleeping' : 'offline')
    }

    setLoading(false)
    setLastCheck(new Date().toLocaleTimeString())
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  const totalEvents = health?.events_last_hour ?? 0
  const typeCounts = health?.type_counts_last_hour || {}

  const formatTime = (isoStr) => {
    if (!isoStr) return '--'
    try {
      const d = new Date(isoStr)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch { return isoStr }
  }

  const getEventColor = (type) => {
    const colors = {
      CRASH: 'var(--status-error, #ff4444)',
      RESTART: 'var(--status-starting, #FF9800)',
      ERROR: 'var(--status-error, #ff4444)',
      STARTUP: 'var(--status-online, #00C853)',
      SHUTDOWN: 'var(--status-warning, #FFD600)',
      HEARTBEAT: 'var(--accent-primary, #00B8D4)',
      WATCHDOG: 'var(--accent-purple, #7C4DFF)',
    }
    return colors[type] || 'var(--text-muted)'
  }

  return (
    <div className={`widget-card ${status}`}>
      <div className="widget-header mhw-header-clickable" onClick={() => setExpanded(!expanded)}>
        <ChevronRight size={14} className={`mhw-chevron ${expanded ? 'rotated' : ''}`} />
        <Activity size={16} className="widget-icon" />
        <span className="widget-title">Monitor Health</span>
        <span className={`status-badge ${status}`}>
          {status === 'online' ? 'HEALTHY' : status === 'warning' ? 'MODERATE' : status === 'error' ? 'ISSUES' : status.toUpperCase()}
        </span>
        <button className={`refresh-btn ${loading ? 'spinning' : ''}`} onClick={(e) => { e.stopPropagation(); fetchData() }} disabled={loading}>
          <RefreshCw size={12} />
        </button>
      </div>

      {expanded && (
        <div className="widget-body">
          {proxyStatus !== 'ok' && (
            <div className={proxyStatus === 'sleeping' ? 'widget-sleeping' : 'widget-empty'}>
              <div>{proxyStatus === 'sleeping' ? 'Space is sleeping...' : 'Unable to fetch health data'}</div>
            </div>
          )}

          {health && (
            <>
              {/* Stats row */}
              <div className="mhw-stats">
                <div className="mhw-stat">
                  <Zap size={14} />
                  <span className="mhw-stat-value">{totalEvents}</span>
                  <span className="mhw-stat-label">Events/hr</span>
                </div>
                <div className="mhw-stat">
                  <AlertTriangle size={14} style={{ color: 'var(--status-error)' }} />
                  <span className="mhw-stat-value">{health.crashes_today ?? 0}</span>
                  <span className="mhw-stat-label">Crashes</span>
                </div>
                <div className="mhw-stat">
                  <RotateCcw size={14} style={{ color: 'var(--status-starting)' }} />
                  <span className="mhw-stat-value">{health.restarts_today ?? 0}</span>
                  <span className="mhw-stat-label">Restarts</span>
                </div>
              </div>

              {/* Event type breakdown */}
              {Object.keys(typeCounts).length > 0 && totalEvents > 0 && (
                <div className="mhw-event-bar">
                  {Object.entries(typeCounts)
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => (
                      <div
                        key={type}
                        className="mhw-event-segment"
                        style={{
                          flex: count,
                          backgroundColor: getEventColor(type),
                        }}
                        title={`${type}: ${count}`}
                      />
                    ))}
                </div>
              )}

              {/* Event type legend */}
              {Object.keys(typeCounts).length > 0 && totalEvents > 0 && (
                <div className="mhw-event-legend">
                  {Object.entries(typeCounts)
                    .filter(([, v]) => v > 0)
                    .map(([type, count]) => (
                      <span key={type} className="mhw-legend-item">
                        <span className="mhw-legend-dot" style={{ backgroundColor: getEventColor(type) }} />
                        {type} ({count})
                      </span>
                    ))}
                </div>
              )}

              {/* Last event */}
              {health.last_event && (
                <div className="mhw-last-event">
                  <div className="mhw-last-event-header">Last Event</div>
                  <div className="mhw-last-event-row">
                    <span
                      className="mhw-event-type-badge"
                      style={{ backgroundColor: getEventColor(health.last_event.type) }}
                    >
                      {health.last_event.type}
                    </span>
                    <span className="mhw-event-source">{health.last_event.source}</span>
                    <span className="mhw-event-time">{formatTime(health.last_event.timestamp)}</span>
                  </div>
                  {health.last_event.message && (
                    <div className="mhw-event-message">{health.last_event.message}</div>
                  )}
                </div>
              )}

              {/* Per-monitor restart counts from last heartbeat */}
              {(() => {
                const restartCounts = health.last_event?.details?.restart_counts
                  || health.restart_state?.monitors
                  || {}
                const entries = Object.entries(restartCounts)
                if (entries.length === 0) return null
                return (
                  <div className="mhw-restart-state">
                    <div className="mhw-restart-header">Monitor Restart Counts (Today)</div>
                    {entries.map(([name, count]) => (
                      <div key={name} className="mhw-restart-row">
                        <span className="mhw-restart-name">
                          {name.replace('mlb_', '').replace('_v003.py', '').replace('.py', '').replace(/_/g, ' ')}
                        </span>
                        <span className={`mhw-restart-count ${count > 3 ? 'high' : count > 0 ? 'some' : 'none'}`}>
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </>
          )}

          <div className="widget-footer">
            <span>{lastCheck ? `Checked: ${lastCheck}` : 'Loading...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default MonitorHealthWidget
